import "../style.css";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "react-datepicker/dist/react-datepicker.css";
import { getMeetAssistPrice } from "../../../../actions/admin/settings"
import MeetAssist from "./MeetAssist";
import Disclaimer from "../../../../components/disclaimer";
import ChildSeatSelection from "./ChildSeatSelection";
import { ArrowRightIcon } from "../../../../components/icons";
import { motion } from "framer-motion";
import { pressable } from "../../../../components/motion";
import ReactTooltip from "react-tooltip";
import PassengerName from "./PassengerName";
import DuplicateBooking from "./DuplicateBooking";
import {
  HANEDA_AIRPORT_PLACE_ID,
  TOKYO_STATION_PLACE_ID,
  buildDefaultMeetAssistState,
  checkLocationUseMeetService,
  checkLocationUseTerminalSelection,
  createMeetAssistPriceStructure,
  getMeetAssistPriceForSelection,
  isNightHours,
} from "../../../../utils/meetAssist";

// Constants
const CHILD_SEAT_PRICE = 1100;

const createDefaultMeetAssistState = () =>
  buildDefaultMeetAssistState({ hasArrival: false, hasDeparture: false });

const updateChildSeatInCart = (cart, bookingIndex, seatIndex, newCount, priceChange) => {
  cart.bookingInfo[bookingIndex].childSeat[seatIndex].childSeatCount = newCount;
  cart.bookingInfo[bookingIndex].childSeatPrice =
    (cart.bookingInfo[bookingIndex].childSeatPrice ?? 0) + priceChange;
};

const getChildSeatPaxCount = (bookingInfo, index, paxType) => {
  const paxItem = bookingInfo[index].pax.find(item => item.paxtype === paxType);
  return paxItem ? paxItem.paxcount : 0;
};

const BookingInformation = ({
  cart,
  updateCartItem,
  checkTokenUser,
  isEditBooking,
  addToCart,
  setShowModalConfirmation,
  lang,
  duplicateBooking,
  isRecurring,
  setIsRecurring,
  inputFields,
  setInputFields,
  inputFieldsToShow,
  setInputFieldsToShow,
  inputFieldsToShowFormated,
  setInputFieldsToShowFormated,
  isConfirmEditBookingDisabled,
  isConfirmBookingDisabled,
  getTooltipWording
}) => {
  const [infantS, setInfantS] = useState([]);
  const [toddlerS, setToddlerS] = useState([]);
  const [childS, setChildS] = useState([]);

  const [isUsingChildSeat, setChildSeat] = useState(false);
  const [pronounced, setPronounced] = useState("Mr.");
  const [meetAssistS, setMeetAssistS] = useState(createDefaultMeetAssistState);

  const { t } = useTranslation();

  const [meetAssistPrice, setMeetAssistPrice] = useState({
    pickup: {
      meetAssistBasePrice: 0,
      vipMeetAssistBasePrice: 0,
      vipMeetAssistAdditionalPrice: 0,
    },
    dropoff: {
      meetAssistBasePrice: 0,
      vipMeetAssistBasePrice: 0,
      vipMeetAssistAdditionalPrice: 0,
    }
  })

  const getTotalPax = () =>
    cart.paxList.adult +
    cart.paxList.child +
    cart.paxList.toddler +
    cart.paxList.baby;

  /**
   * Settle both Meet & Assist legs once the prices are in.
   *
   * A choice the customer already made -- they came back to this page, or the
   * cart was seeded on the vehicle-selection page -- wins over the location's
   * default. Either way the price is recalculated here rather than trusted from
   * the cart, so a rate changed in the admin dashboard since the cart was
   * created cannot be undercharged.
   */
  const applyMeetAssistDefaults = () => {
    if (!cart) return;

    const totalPax = getTotalPax();
    const defaults = buildDefaultMeetAssistState({
      pickupLocation: cart.pickupLocation,
      dropoffLocation: cart.destinations?.[cart.destinations.length - 1],
      hasArrival: Boolean(cart.isFromAirport || cart.isFromStation),
      hasDeparture: Boolean(cart.isToAirport || cart.isToStation),
      meetAssistPrice,
    });

    const settled = defaults.map((leg, index) => {
      const chosen = cart.meetAssist?.[index]?.meetAssistName;
      const selection = chosen || leg.meetAssistName;

      return {
        ...leg,
        meetAssistName: selection,
        meetAssistPrice: getMeetAssistPriceForSelection(
          index === 0 ? meetAssistPrice.pickup : meetAssistPrice.dropoff,
          selection,
          totalPax
        ),
      };
    });

    setMeetAssistS(settled);
    cart.meetAssist = settled;
    updateCartItem(cart);
  };

  const checkIsPickupTokyoStation = () => {
    return cart?.isStationTransfer && cart?.isFromStation && cart?.pickupLocation.place_id === TOKYO_STATION_PLACE_ID;
  };

  const checkIsPickupHaneda = () => {
    return cart?.isAirportTransfer && cart?.isFromAirport && cart?.pickupLocation.place_id === HANEDA_AIRPORT_PLACE_ID;
  };

  const checkIsDropoffHaneda = () => {
    return cart?.isAirportTransfer && cart?.isToAirport && cart?.destinations[cart?.destinations.length - 1].place_id === HANEDA_AIRPORT_PLACE_ID;
  };

  const fetchMeetAssistPrice = async () => {
    try {
      const prices = await getMeetAssistPrice();
      const isPickupNight = isNightHours(cart.pickupDateTime);
      const isDropoffNight = isNightHours(cart.customerDropoffDateTime);

      const priceStructure = createMeetAssistPriceStructure(prices, isPickupNight, isDropoffNight);
      setMeetAssistPrice(priceStructure);
    } catch (error) {
      console.error("Error fetching meet assist price:", error);
    }
  };

  useEffect(() => {
    const checkToken = async () => {
      const jwtDecoded = await checkTokenUser();

      if (
        !jwtDecoded ||
        !jwtDecoded.user ||
        !jwtDecoded.user.id ||
        !["individual", "business"].includes(jwtDecoded.user.id.role)
      ) {
        return;
      }

      const accountDetail =
        jwtDecoded.user.id[
        jwtDecoded.user.id.role === "individual"
          ? "individualDetail"
          : "businessDetail"
        ];

      if (!accountDetail?.billingInformation) return;

      updateCartItem({
        ...cart,
        passengerName: accountDetail.billingInformation.name,
      });

      setPronounced(accountDetail.billingInformation.pronounced);
    };

    checkToken();
    fetchMeetAssistPrice();
  }, []);

  useEffect(() => {
    applyMeetAssistDefaults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetAssistPrice])

  useEffect(() => {
    if (cart) {
      const infantSizeSeatArray = cart.bookingInfo.map(
        (item) => item.childSeat[0].childSeatCount
      );
      const toddlerSizeSeatArray = cart.bookingInfo.map(
        (item) => item.childSeat[1].childSeatCount
      );
      const childSizeSeatArray = cart.bookingInfo.map(
        (item) => item.childSeat[2].childSeatCount
      );

      setInfantS(infantSizeSeatArray);
      setToddlerS(toddlerSizeSeatArray);
      setChildS(childSizeSeatArray);
    }
  }, []);

  const onChangeRequestForm = (e) => {
    const obj = {
      note: e.target.value,
    };

    cart.note_arr[0] = obj;
    updateCartItem(cart);
  };

  const onChangeCartSelection = (e) => {
    cart[e.name] = e.value;
    updateCartItem(cart);
  }

  const onChangeCartText = (e) => {
    cart[e.target.name] = e.target.value;
    updateCartItem(cart);
  }

  const onChangeMeetAssist = (type, value) => {
    const index = type === "arrival" ? 0 : 1;
    const legPrices = index === 0 ? meetAssistPrice.pickup : meetAssistPrice.dropoff;

    const meetAssistService = meetAssistS.map(item => ({ ...item }));
    meetAssistService[index].meetAssistName = value;
    meetAssistService[index].meetAssistPrice = getMeetAssistPriceForSelection(
      legPrices,
      value,
      getTotalPax()
    );

    setMeetAssistS(meetAssistService);
    cart.meetAssist = meetAssistService;
    updateCartItem(cart);
  };

  const clickChildSeat = () => {
    if (isUsingChildSeat) {
      //remove child seat
      const newArray = new Array(cart.bookingInfo.length).fill(0);

      setInfantS(newArray);
      setToddlerS(newArray);
      setChildS(newArray);

      setChildSeat(false);
    } else {
      //add child seat
      setChildSeat(true);
    }
  };

  const incrementChildSeat = (type, ind) => {
    const seatConfig = {
      infant: { paxType: "Baby", seatIndex: 0, stateArray: infantS, setStateArray: setInfantS },
      toddler: { paxType: "Toddler", seatIndex: 1, stateArray: toddlerS, setStateArray: setToddlerS },
      child: { paxType: "Child", seatIndex: 2, stateArray: childS, setStateArray: setChildS }
    };

    const config = seatConfig[type];
    if (!config) return;

    const paxCount = getChildSeatPaxCount(cart.bookingInfo, ind, config.paxType);
    const currentCount = config.stateArray[ind] ?? 0;

    if (paxCount >= currentCount + 1) {
      const newArray = [...config.stateArray];
      if (newArray[ind] === undefined) {
        newArray[ind] = 0;
      }
      const newCount = newArray[ind] + 1;

      updateChildSeatInCart(cart, ind, config.seatIndex, newCount, CHILD_SEAT_PRICE);
      newArray[ind] = newCount;
      config.setStateArray(newArray);
      updateCartItem(cart);
    }
  };

  const decrementChildSeat = (type, ind) => {
    const seatConfig = {
      infant: { seatIndex: 0, stateArray: infantS, setStateArray: setInfantS },
      toddler: { seatIndex: 1, stateArray: toddlerS, setStateArray: setToddlerS },
      child: { seatIndex: 2, stateArray: childS, setStateArray: setChildS }
    };

    const config = seatConfig[type];
    if (!config) return;

    const newArray = [...config.stateArray];
    if (newArray[ind] === undefined) {
      newArray[ind] = 0;
    }

    if (newArray[ind] > 0) {
      const newCount = newArray[ind] - 1;

      updateChildSeatInCart(cart, ind, config.seatIndex, newCount, -CHILD_SEAT_PRICE);
      newArray[ind] = newCount;
      config.setStateArray(newArray);
      updateCartItem(cart);
    }
  };

  const checkChild = (pax) => {
    for (let i = 1; i < pax.length; i++) {
      if (pax[i].paxcount > 0) {
        return true;
      }
    }
    if (pax.child > 0 || pax.toddler > 0 || pax.baby > 0) return true;
    return false;
  };

  const formatDisplayDate = (date) => {
    const m = moment(date);
    return lang === "EN"
      ? m.format("MMM. Do, YYYY (ddd)")
      : m.format("YYYY年M月D日（ddd）");
  };

  const onChangeDuplicateDate = (date, index) => {
    if (!date) return;

    // ✅ Normalize to Date
    const parsedDate =
      date instanceof Date ? date : new Date(date);

    if (isNaN(parsedDate.getTime())) return;

    const formattedISO = parsedDate.toLocaleDateString("sv-SE", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

    setInputFields((prev) => {
      const next = [...prev];
      next[index] = formattedISO; // string (YYYY-MM-DD)
      return next;
    });

    setInputFieldsToShow((prev) => {
      const next = [...prev];
      next[index] = parsedDate; // Date object
      return next;
    });

    setInputFieldsToShowFormated((prev) => {
      const next = [...prev];
      next[index] = formatDisplayDate(parsedDate);
      return next;
    });
  };

  const removeDateByIndex = (index) => {
    setInputFields((prev) =>
      prev.length === 1
        ? [""]
        : prev.filter((_, i) => i !== index)
    );

    setInputFieldsToShow((prev) =>
      prev.length === 1
        ? [""]
        : prev.filter((_, i) => i !== index)
    );

    setInputFieldsToShowFormated((prev) =>
      prev.length === 1
        ? [""]
        : prev.filter((_, i) => i !== index)
    );
  };


  const handleAddDate = () => {
    if (inputFields[inputFields.length - 1] !== "") {
      setInputFields((prev) => [...prev, ""]);
      setInputFieldsToShow((prev) => [...prev, ""]);
      setInputFieldsToShowFormated((prev) => [...prev, ""]);
    }
  };

  return (
    <div className="tw:relative tw:flex tw:flex-col tw:min-w-0 tw:bg-white tw:bg-clip-border tw:border tw:border-[rgba(0,0,0,0.125)] tw:rounded-[0.3rem] tw:-mx-5 tw:md:mx-0">
      <div className="tw:flex-1 tw:p-4 tw:m-3">
        <MeetAssist
          pickup={cart.isFromAirport || cart.isFromStation}
          dropoff={cart.isToAirport || cart.isToStation}
          pickupType={cart.isFromAirport ? "airport" : "station"}
          dropoffType={cart.isToAirport ? "airport" : "station"}
          meetAssistS={meetAssistS}
          cart={cart}
          onChangeMeetAssist={onChangeMeetAssist}
          meetAssistPrice={meetAssistPrice}
          isPickupTokyoStation={checkIsPickupTokyoStation()}
          isPickupHaneda={checkIsPickupHaneda()}
          isDropoffHaneda={checkIsDropoffHaneda()}
          onChangeCartSelection={(e) => onChangeCartSelection(e)}
          onChangeCartText={(e) => onChangeCartText(e)}
          isPickupUseMeetService={checkLocationUseMeetService(cart.pickupLocation)}
          isDropoffUseMeetService={checkLocationUseMeetService(cart.destinations[cart.destinations.length - 1])}
          isPickupUseTerminalSelection={checkLocationUseTerminalSelection(cart.pickupLocation)}
          isDropoffUseTerminalSelection={checkLocationUseTerminalSelection(cart.destinations[cart.destinations.length - 1])}
        />

        <PassengerName
          cart={cart}
          pronounced={pronounced}
          onChangeCartSelection={onChangeCartSelection}
          onChangeCartText={onChangeCartText}
          t={t}
        />

        {checkChild(cart?.paxList) && (
          <ChildSeatSelection
            cart={cart}
            infantS={infantS}
            toddlerS={toddlerS}
            childS={childS}
            clickChildSeat={clickChildSeat}
            isUsingChildSeat={checkChild(cart?.paxList)}
            decrementChildSeat={decrementChildSeat}
            incrementChildSeat={incrementChildSeat}
          />
        )}

        <div className="tw:!mb-5">
          <div className="tw:!mb-2">
            <p className="tw:!m-0 tw:!font-[400] tw:!text-[15px] noto">
              {t("BookingConfirmation.MessageInquiry.Title")}
            </p>
          </div>
          <textarea
            onChange={(e) => onChangeRequestForm(e)}
            className="tw:block tw:w-full tw:text-[14px] tw:leading-normal tw:text-[#4e4e4e] tw:bg-white tw:border tw:border-[#f1f1f1] tw:rounded-[0.3rem] tw:py-[0.375rem] tw:pr-3 tw:pl-[1.1rem] tw:[font-family:var(--font-sans-primary)] tw:transition-[border-color,box-shadow] tw:duration-200 tw:focus:border-[#DBBE78] tw:focus:outline-none tw:focus:shadow-[0_0_0_3px_rgba(219,190,120,0.18)] tw:disabled:bg-[rgba(167,167,167,0.1)] form-insert tw:!h-[120px] tw:!pr-4"
            placeholder={t(
              "BookingConfirmation.MessageInquiry.Placeholder"
            )}
            defaultValue={cart?.note_arr[0]?.note}
          />
        </div>

        {/* DISCLAMER */}
        <div className="tw:!mb-5">
          <div className="tw:!mb-2">
            <p className="tw:!m-0 tw:!font-[400] tw:!text-[15px] noto">
              {t("componentDisclaimer.title")}
            </p>
          </div>
          <div className="bg-gold-grey-F9F9F9 tw:p-6 tw:rounded-[0.5rem]">
            <div
              className="tw:overflow-auto custom-scrollbar"
              style={{ height: "auto" }}
            >
              <Disclaimer
                textClassName={'tw:!text-[14px] tw:!text-[#4E4E4E] tw:!text-[400]'}
                p4ClassName={'tw:!mb-0'}
              />
            </div>
          </div>
        </div>

        <DuplicateBooking
          lang={lang}
          isRecurring={isRecurring}
          setIsRecurring={setIsRecurring}
          inputFields={inputFields}
          setInputFields={setInputFields}
          inputFieldsToShow={inputFieldsToShow}
          setInputFieldsToShow={setInputFieldsToShow}
          inputFieldsToShowFormated={inputFieldsToShowFormated}
          setInputFieldsToShowFormated={setInputFieldsToShowFormated}
          onChangeDuplicateDate={onChangeDuplicateDate}
          removeDateByIndex={removeDateByIndex}
          handleAddDate={handleAddDate}
        />

        {/* BUTTON */}
        <div
          className="tw:!flex tw:!gap-2 tw:!hidden tw:md:!block"
          data-tip
          data-for={"disableButtonTooltip"}
        >
          <motion.button
            type="button"
            whileTap={pressable.tap}
            className={`tw:inline-block tw:font-normal tw:leading-normal tw:text-center tw:no-underline tw:align-middle tw:cursor-pointer tw:select-none tw:border tw:border-transparent tw:px-3 tw:py-[0.375rem] tw:text-base tw:rounded-[0.25rem] tw:transition-[color,background-color,border-color,box-shadow] tw:duration-150 tw:disabled:opacity-65 tw:disabled:pointer-events-none tw:!bg-[#DBBE78] tw:!rounded tw:!py-2 tw:w-full tw:!w-6/6 tw:md:!w-6/6 `}
            onClick={() => {
              if (isEditBooking) {
                addToCart("/Cart")
              } else {
                if (isRecurring) {
                  duplicateBooking();
                } else {
                  setShowModalConfirmation(true);
                }
              }
            }}
            disabled={isEditBooking ? isConfirmEditBookingDisabled(cart) : isConfirmBookingDisabled(cart)}
          >
            <div className="tw:!flex tw:!items-center tw:!justify-center tw:!gap-2 tw:!text-[#FFFFFF] tw:!text-[15px] tw:!font-[700]">
              {t("BookingConfirmation.ConfirmBooking")}
              <ArrowRightIcon color="#FFFFFF" size={20} />
            </div>
          </motion.button>
          {
            isConfirmBookingDisabled(cart) && (
              <ReactTooltip
                id="disableButtonTooltip"
                backgroundColor="#4A4A4A"
                textColor="#FFFFFF"
                data-tooltip-place="top"
                data-tooltip-position-strategy="fixed"
              >
                <span
                  style={{ textAlign: "center", display: "block" }}
                  dangerouslySetInnerHTML={{
                    __html: t(getTooltipWording(cart)),
                  }}
                />
              </ReactTooltip>

            )
          }
        </div>
      </div>
    </div>
  );
};

export default BookingInformation;